/**
 * usePreviewMetricSort — local (view-only) sort state for a Preview list.
 * Clicking a Current/New/Diff header cycles desc → asc → off; `sortEntries`
 * returns a sorted COPY of the list's `{ entry, index }` array (the original
 * index is preserved, so edits/removes still target the right item).
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { useCallback, useState } from 'react';

import {
  comparePreviewEntries,
  cyclePreviewSort,
  type PreviewMetricSort,
  type PreviewSortableEntry,
  type PreviewSortColumn,
} from '../preview-sort';

export interface UsePreviewMetricSort {
  sort: PreviewMetricSort | null;
  cycle: (column: PreviewSortColumn) => void;
  sortEntries: <T>(
    entries: readonly T[],
    toSortable: (entry: T) => PreviewSortableEntry,
  ) => T[];
}

export function usePreviewMetricSort(): UsePreviewMetricSort {
  const [sort, setSort] = useState<PreviewMetricSort | null>(null);

  const cycle = useCallback(
    (column: PreviewSortColumn) => setSort((prev) => cyclePreviewSort(prev, column)),
    [],
  );

  const sortEntries = useCallback(
    <T>(entries: readonly T[], toSortable: (entry: T) => PreviewSortableEntry): T[] => {
      const decorated = entries.map((entry) => ({ entry, key: toSortable(entry) }));
      decorated.sort((a, b) => comparePreviewEntries(a.key, b.key, sort));
      return decorated.map((d) => d.entry);
    },
    [sort],
  );

  return { sort, cycle, sortEntries };
}
